import { SETTLED_FINAL_INVOICE_STATUS } from 'constants/InterventionConstants';
import { SELF_SERVICE_KEY_STATUS, SELF_SERVICE_KEY_TYPES, SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';
import { COLUMN } from 'modules/interventions/constants/kanban';
import * as kanbanUtils from 'modules/interventions/kanban.utils';
import { DASHBOARD_FIELDS } from 'modules/users/constants';
import { humanize } from 'utils/global';

/** @typedef {'FINAL_INVOICE' | 'SENT_STATUS' | 'KEYS' | 'MOBILITY' } KanbanIconTypeEnum */

export const KANBAN_ICON_TYPE = {
  FINAL_INVOICE: 'FINAL_INVOICE',
  SENT_STATUS: 'SENT_STATUS',
  KEYS: 'KEYS',
  MOBILITY: 'MOBILITY',
};

export const DASHBOARD_FIELD_BY_KANBAN_ICON_TYPE = {
  [KANBAN_ICON_TYPE.KEYS]: DASHBOARD_FIELDS.CUSTOMER_KEY,
  [KANBAN_ICON_TYPE.MOBILITY]: DASHBOARD_FIELDS.MOBILITY,
  [KANBAN_ICON_TYPE.FINAL_INVOICE]: DASHBOARD_FIELDS.FINAL_INVOICE_STATUS,
  [KANBAN_ICON_TYPE.SENT_STATUS]: DASHBOARD_FIELDS.STATUS,
};

/**
 * @param {Pick<import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention, 'selfServices'>} selfServices
 * @param {import('modules/selfServices/types').SelfServiceTypeEnum} selfServiceType
 * @returns {import('modules/selfServices/types').KeyStatusEnum}
 */
const getKeyStatus = (selfServices, selfServiceType) =>
  selfServices?.find(({ type }) => type === selfServiceType)?.keyStatus;

const getKeyType = (selfServices, selfServiceType) =>
  selfServices?.find(({ type }) => type === selfServiceType)?.keyType;

/**
 * @typedef {import('react-intl').MessageDescriptor & {values?: (intervention: import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention) => Object}} Title
 *
 * @typedef {{
 *  title: Title | ((selfServiceType: import('modules/selfServices/types').SelfServiceTypeEnum) => Title),
 *  className: string,
 *  color?: string,
 *  icon: string,
 *  dataCy: string,
 *  type: KanbanIconTypeEnum,
 *  isVisible: (intervention: import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention, kanbanStatus: import('modules/interventions/types/interfaces/kanban').KanbanEnum) => boolean,
 * }} KanbanIcon
 */

/** @type {Array<KanbanIcon>} */
const kanbanIcons = [
  {
    title: selfServiceType => ({
      id: `intervention_self_service_status.${selfServiceType.toLowerCase()}-sent`,
      defaultMessage: `${humanize(selfServiceType.toLowerCase())} sent`,
    }),
    className: 'text-white',
    color: '#84B026',
    icon: 'fb-icon--sent',
    dataCy: 'kanban-sent-icon',
    type: KANBAN_ICON_TYPE.SENT_STATUS,
    isVisible: (intervention, kanbanStatus) => kanbanUtils.canShowSendIcon(intervention, kanbanStatus),
  },
  {
    title: {
      id: 'final_invoice.status.paid',
      defaultMessage: 'Invoice paid',
    },
    className: 'success',
    icon: 'fb-icon--credit-card-checkmark',
    dataCy: 'kanban-invoice-paid-icon',
    type: KANBAN_ICON_TYPE.FINAL_INVOICE,
    isVisible: ({ finalInvoiceStatus }) => SETTLED_FINAL_INVOICE_STATUS.includes(finalInvoiceStatus),
  },
  {
    title: {
      id: 'final_invoice.status.added',
      defaultMessage: 'Invoice added',
    },
    className: 'awaiting',
    icon: 'fb-icon--credit-card-send',
    dataCy: 'kanban-invoice-added-icon',
    type: KANBAN_ICON_TYPE.FINAL_INVOICE,
    isVisible: ({ finalInvoiceStatus, invoicePaymentAllowed }, kanbanStatus) =>
      invoicePaymentAllowed &&
      kanbanStatus !== COLUMN.CHECK_OUT_COMPLETED &&
      Boolean(finalInvoiceStatus) &&
      !SETTLED_FINAL_INVOICE_STATUS.includes(finalInvoiceStatus),
  },
  {
    title: {
      id: 'final_invoice.information_only',
      defaultMessage: 'Invoice for information only',
    },
    className: 'awaiting',
    icon: 'fb-icon--credit-card-send',
    dataCy: 'kanban-invoice-informational-icon',
    type: KANBAN_ICON_TYPE.FINAL_INVOICE,
    isVisible: ({ finalInvoiceStatus, invoicePaymentAllowed }) =>
      !invoicePaymentAllowed && (Boolean(finalInvoiceStatus) && !SETTLED_FINAL_INVOICE_STATUS.includes(finalInvoiceStatus)),
  },
  {
    title: {
      id: 'kiosk.keysInSlot',
      defaultMessage: 'Keys in kiosk {kioskName}',
      values: ({ selfServices }) => ({
        kioskName: selfServices.find(({ type }) => [SELF_SERVICE_TYPE.CHECK_IN, SELF_SERVICE_TYPE.BREAKDOWN].includes(type)).slot.kiosk.name,
      }),
    },
    className: 'key-ready',
    icon: 'fb-icon--Key-box',
    dataCy: 'kanban-key-check-in-icon',
    type: KANBAN_ICON_TYPE.KEYS,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.CHECK_IN_COMPLETED &&
      selfServices.find(({ type }) => [SELF_SERVICE_TYPE.CHECK_IN, SELF_SERVICE_TYPE.BREAKDOWN].includes(type)).keyStatus ===
      SELF_SERVICE_KEY_STATUS.IN_SLOT,
  },
  {
    title: {
      id: 'kiosk.keysMissing',
      defaultMessage: 'Keys missing',
    },
    className: 'key-missing',
    icon: 'fb-icon--hand-check-in',
    dataCy: 'kanban-key-missing-icon',
    type: KANBAN_ICON_TYPE.KEYS,
    isVisible: ({ selfServices }, kanbanStatus) => {
      const checkOut = selfServices?.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_OUT);
      return (
        (kanbanStatus === COLUMN.IN_HOUSE || kanbanStatus === COLUMN.CHECK_OUT_READY) &&
        checkOut?.keyStatus === SELF_SERVICE_KEY_STATUS.NONE &&
        checkOut?.keyDepositEnabled
      );
    },
  },
  {
    title: {
      id: 'kiosk.keysInSlot',
      defaultMessage: 'Keys in kiosk {kioskName}',
      values: ({ selfServices }) => ({
        kioskName: selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_OUT).slot.kiosk.name,
      }),
    },
    className: 'awaiting',
    icon: 'fb-icon--hand-check-in',
    dataCy: 'kanban-key-check-out-icon',
    type: KANBAN_ICON_TYPE.KEYS,
    isVisible: ({ selfServices }, kanbanStatus) =>
      (kanbanStatus === COLUMN.IN_HOUSE || kanbanStatus === COLUMN.CHECK_OUT_READY) &&
      selfServices &&
      getKeyStatus(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_STATUS.IN_SLOT,
  },
  {
    title: {
      id: 'kiosk.keysRetrieved',
      defaultMessage: 'Keys retrieved',
    },
    className: 'key-ready',
    icon: 'fb-icon--hand-check-out',
    dataCy: 'kanban-key-retrieved-icon',
    type: KANBAN_ICON_TYPE.KEYS,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
      getKeyStatus(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_STATUS.RETRIEVED,
  },
  {
    title: {
      id: 'kiosk.keysInSlot',
      defaultMessage: 'Keys in kiosk {kioskName}',
      values: ({ selfServices }) => ({
        kioskName: selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_OUT).slot.kiosk.name,
      }),
    },
    className: 'alertColor',
    icon: 'fb-icon--hand-check-out',
    dataCy: 'kanban-key-not-retrieved-icon',
    type: KANBAN_ICON_TYPE.KEYS,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
      getKeyStatus(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_STATUS.IN_SLOT &&
      getKeyType(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_TYPES.CUSTOMER,
  },
  {
    title: {
      id: 'final_invoice.status.awaiting',
      defaultMessage: 'Invoice not paid',
    },
    className: 'alertColor',
    icon: 'fb-icon--credit-card-waiting',
    dataCy: 'kanban-invoice-awaiting-icon',
    type: KANBAN_ICON_TYPE.FINAL_INVOICE,
    isVisible: ({ finalInvoiceStatus, invoicePaymentAllowed }, kanbanStatus) =>
      invoicePaymentAllowed &&
      kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
      Boolean(finalInvoiceStatus) &&
      !SETTLED_FINAL_INVOICE_STATUS.includes(finalInvoiceStatus),
  },
  {
    title: {
      id: 'kiosk.mobilityPrepared',
      defaultMessage: 'Mobility keys prepared',
    },
    className: 'success',
    icon: 'fb-icon--mobility-key',
    dataCy: 'kanban-mobility-key-prepared',
    type: KANBAN_ICON_TYPE.MOBILITY,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.HOME_CHECK_IN_COMPLETED &&
      getKeyType(selfServices, SELF_SERVICE_TYPE.CHECK_IN) === SELF_SERVICE_KEY_TYPES.MOBILITY,
  },
  {
    title: {
      id: 'kiosk.mobilityToPrepare',
      defaultMessage: 'Mobility keys to prepare',
    },
    className: 'alertColor',
    icon: 'fb-icon--mobility-key',
    dataCy: 'kanban-mobility-key-to-prepare',
    type: KANBAN_ICON_TYPE.MOBILITY,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.HOME_CHECK_IN_COMPLETED &&
      selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN).mobilityEnabled &&
      getKeyStatus(selfServices, SELF_SERVICE_TYPE.CHECK_IN) === SELF_SERVICE_KEY_STATUS.NONE,
  },
  {
    title: {
      id: 'kiosk.mobilityToRetrieve',
      defaultMessage: 'Mobility keys to retrieve',
    },
    className: 'alertColor',
    icon: 'fb-icon--mobility-key',
    dataCy: 'kanban-mobility-key-to-retrieve',
    type: KANBAN_ICON_TYPE.MOBILITY,
    isVisible: ({ selfServices }, kanbanStatus) =>
      kanbanStatus === COLUMN.CHECK_OUT_COMPLETED &&
      getKeyType(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_TYPES.MOBILITY &&
      getKeyStatus(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) === SELF_SERVICE_KEY_STATUS.IN_SLOT,
  },
  {
    title: {
      id: 'kiosk.mobilityActivated',
      defaultMessage: 'Mobility activated',
    },
    className: 'awaiting',
    icon: 'fb-icon--mobility',
    dataCy: 'kanban-mobility-key-activated',
    type: KANBAN_ICON_TYPE.MOBILITY,
    isVisible: ({ selfServices }, kanbanStatus) => {
      const selfService = selfServices?.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN);

      return kanbanStatus !== COLUMN.HOME_CHECK_IN_COMPLETED &&
        // We want to ensure `mobility` exists before going further
        // (ie. we dont want the next test to pass if `mobility` is undefined)
        selfService?.mobility &&
        // customerAccepted can be undefined, and we want to make sure the customer said no
        selfService.mobility.customerAccepted !== false &&
        getKeyType(selfServices, SELF_SERVICE_TYPE.CHECK_OUT) !== SELF_SERVICE_KEY_TYPES.MOBILITY;
    },
  }
];

export default kanbanIcons;
